import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Icon } from '@tviklax/ui-components';
import { API_BASE_URL } from '../../../shared/constants/env';

type MessagingMode = 'page' | 'drawer' | 'widget';

type BrowserSpeechRecognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: ((event: any) => void) | null;
};

const ACCENT_ORANGE = '#f97316';
const ACCENT_ORANGE_DARK = '#ea580c';
const ACCENT_ORANGE_DEEP = '#c2410c';
const ACCENT_ORANGE_BORDER = '#fdba74';
const ACCENT_ORANGE_SOFT = '#fff7ed';
const ACCENT_ORANGE_SOFT_ALT = '#ffedd5';
const ACCENT_ORANGE_TEXT = '#9a3412';
const ACCENT_ORANGE_RING = 'rgba(249, 115, 22, 0.24)';

const MessagingContainer = styled.div<{ $mode: MessagingMode }>`
  display: ${({ $mode }) => ($mode === 'page' ? 'grid' : 'flex')};
  grid-template-columns: ${({ $mode }) => ($mode === 'page' ? 'minmax(0, 1fr)' : 'none')};
  grid-template-rows: ${({ $mode }) => ($mode === 'page' ? 'minmax(0, 1fr)' : 'none')};
  flex-direction: ${({ $mode }) => ($mode === 'page' ? 'row' : 'column')};
  gap: 16px;
  flex: 1;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  overflow: hidden;
  background: ${({ $mode }) => ($mode === 'page' ? '#f3f2f1' : 'transparent')};
  border-radius: ${({ $mode }) => ($mode === 'page' ? '14px' : '0')};

  @media (max-width: 980px) {
    grid-template-columns: ${({ $mode }) => ($mode === 'page' ? 'minmax(0, 1fr)' : 'none')};
  }
`;

const PageShell = styled.div`
  flex: 1;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TeamsShell = styled.div<{ $mode: MessagingMode }>`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-columns: ${({ $mode }) => ($mode === 'widget' ? 'minmax(0, 1fr)' : '60px minmax(220px, 260px) minmax(0, 1fr)')};
  grid-template-rows: ${({ $mode }) => ($mode === 'widget' ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)')};
  border: 1px solid #e1dfdd;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;

  @media (max-width: 1280px) {
    grid-template-columns: ${({ $mode }) => ($mode === 'widget' ? 'minmax(0, 1fr)' : '56px minmax(200px, 230px) minmax(0, 1fr)')};
  }

  @media (max-width: 960px) {
    grid-template-columns: ${({ $mode }) => ($mode === 'widget' ? 'minmax(0, 1fr)' : '56px minmax(0, 1fr)')};
  }
`;

const LeftRail = styled.aside`
  background: #1f2937;
  border-right: 1px solid #111827;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
`;

const RailButton = styled.button<{ $active?: boolean }>`
  width: 42px;
  height: 42px;
  border: 1px solid ${(props) => (props.$active ? ACCENT_ORANGE_BORDER : '#374151')};
  background: ${(props) => (props.$active ? ACCENT_ORANGE : '#111827')};
  color: #ffffff;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 140ms ease, border-color 140ms ease;

  &:hover {
    border-color: ${ACCENT_ORANGE_BORDER};
    background: ${ACCENT_ORANGE_DARK};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${ACCENT_ORANGE_RING};
  }
`;

const ListPane = styled.section`
  border-right: 1px solid #e5e7eb;
  background: #f8fafc;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  @media (max-width: 960px) {
    display: none;
  }
`;

const WidgetTopControls = styled.div`
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WidgetTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const WidgetRailInline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const WidgetThreadSelect = styled.select`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: #111827;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: ${ACCENT_ORANGE};
    box-shadow: 0 0 0 3px ${ACCENT_ORANGE_RING};
  }
`;

const ListHeader = styled.div`
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ListTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
`;

const ListBody = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ListItem = styled.button<{ $active?: boolean }>`
  border: 1px solid ${(props) => (props.$active ? ACCENT_ORANGE_BORDER : '#e5e7eb')};
  background: ${(props) => (props.$active ? ACCENT_ORANGE_SOFT : '#ffffff')};
  border-radius: 10px;
  padding: 10px;
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 3px;
  transition: border-color 140ms ease, background 140ms ease;

  &:hover {
    border-color: ${ACCENT_ORANGE_BORDER};
    background: ${ACCENT_ORANGE_SOFT};
  }
`;

const ListItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const ListItemRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const UnreadBadge = styled.span`
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  padding: 0 6px;
  background: #fee2e2;
  color: #9f1239;
  border: 1px solid #fecdd3;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ListItemRemoveButton = styled.button`
  border: 1px solid #dbe3f1;
  background: #fffaf5;
  color: ${ACCENT_ORANGE_DARK};
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${ACCENT_ORANGE_SOFT};
    border-color: ${ACCENT_ORANGE_BORDER};
  }
`;

const ListItemTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
`;

const ListItemMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ConversationPane = styled.section`
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  background: #ffffff;
  overflow: hidden;
`;

const ComposePanel = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ComposeHint = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const ContactPickList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ToBox = styled.div`
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  padding: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ToLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  padding-top: 4px;
`;

const ToChipWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
`;

const ToChip = styled.button`
  border: 1px solid ${ACCENT_ORANGE_BORDER};
  background: ${ACCENT_ORANGE_SOFT};
  color: ${ACCENT_ORANGE_DARK};
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
`;

const ContactPickItem = styled.button<{ $selected?: boolean }>`
  border: 1px solid ${(props) => (props.$selected ? ACCENT_ORANGE : '#d1d5db')};
  background: ${(props) => (props.$selected ? ACCENT_ORANGE_SOFT : '#ffffff')};
  border-radius: 10px;
  padding: 10px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const PanelHeader = styled.div<{ $mode: MessagingMode }>`
  padding: ${({ $mode }) => ($mode === 'widget' ? '10px 12px 8px' : '14px 16px 10px')};
  border-bottom: 1px solid #edebe9;
  display: flex;
  flex-direction: column;
  gap: ${({ $mode }) => ($mode === 'widget' ? '6px' : '10px')};
  flex-shrink: 0;
  background: #ffffff;
`;

const HeaderTopRow = styled.div<{ $mode: MessagingMode }>`
  display: flex;
  justify-content: space-between;
  align-items: ${({ $mode }) => ($mode === 'widget' ? 'flex-start' : 'center')};
  gap: ${({ $mode }) => ($mode === 'widget' ? '8px' : '12px')};
`;

const ActiveChatTitle = styled.div<{ $mode: MessagingMode }>`
  font-size: ${({ $mode }) => ($mode === 'widget' ? '14px' : '18px')};
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${({ $mode }) => ($mode === 'widget' ? 'nowrap' : 'normal')};
`;

const ActiveChatParticipants = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderMeta = styled.div<{ $mode: MessagingMode }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $mode }) => ($mode === 'widget' ? '6px' : '8px')};
  flex-wrap: ${({ $mode }) => ($mode === 'widget' ? 'nowrap' : 'wrap')};
  justify-content: ${({ $mode }) => ($mode === 'widget' ? 'flex-start' : 'flex-end')};
  overflow: hidden;
  min-height: 28px;

  @media (max-width: 1180px) {
    gap: 6px;
  }
`;

const LangBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #4b5563;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;

  @media (max-width: 1180px) {
    display: none;
  }
`;

const CompactMetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #4b5563;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  white-space: nowrap;
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SearchField = styled.input`
  width: 100%;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  color: #0f172a;
  background: #ffffff;
  transition: border-color 140ms ease, box-shadow 140ms ease;

  &:hover {
    border-color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: ${ACCENT_ORANGE};
    box-shadow: 0 0 0 3px ${ACCENT_ORANGE_RING};
  }
`;

const ConversationAction = styled.button`
  border: 1px solid ${ACCENT_ORANGE_DARK};
  background: ${ACCENT_ORANGE_DARK};
  color: #ffffff;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease, box-shadow 140ms ease;

  &:hover {
    background: ${ACCENT_ORANGE_DEEP};
    border-color: ${ACCENT_ORANGE_DEEP};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${ACCENT_ORANGE_RING};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #dbe3f1;
  background: #fffaf5;
  color: ${ACCENT_ORANGE_DARK};
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s;

  &:hover {
    background: ${ACCENT_ORANGE_SOFT};
    border-color: ${ACCENT_ORANGE_DARK};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MessagesArea = styled.div`
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  overscroll-behavior: contain;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 999px;
  }
`;

const MessageDateSeparator = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  margin: 4px 0 8px;
  pointer-events: none;
`;

const MessageDateChip = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #616161;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e1dfdd;
  border-radius: 999px;
  padding: 4px 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  white-space: nowrap;
`;

const EmptyWorkspace = styled.div`
  min-height: 280px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  padding: 20px;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  background:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 30%),
    linear-gradient(180deg, rgba(248, 250, 252, 0.95), rgba(255, 255, 255, 0.98));
`;

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
`;

const EmptyText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
  max-width: 540px;
`;

const MilestoneGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`;

const MilestoneCard = styled.div`
  border-radius: 14px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid ${ACCENT_ORANGE_SOFT_ALT};
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.04);
`;

const MilestoneLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const MilestoneValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 6px;
`;

const MessageBubble = styled.div<{ $own?: boolean }>`
  align-self: ${(props) => (props.$own ? 'flex-end' : 'flex-start')};
  background: ${(props) => (props.$own ? '#fff7ed' : '#f8fafc')};
  color: #111827;
  border-radius: 14px;
  max-width: 72%;
  width: fit-content;
  padding: 14px;
  border: 1px solid ${(props) => (props.$own ? '#fdba74' : '#dbe3f1')};
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
  overflow: visible;

  @media (max-width: 760px) {
    max-width: 92%;
  }
`;

const MessageCluster = styled.div<{ $own?: boolean; $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$own ? 'flex-end' : 'flex-start')};
  gap: ${(props) => (props.$compact ? '4px' : '8px')};
`;

const MessageText = styled.div`
  font-size: 16px;
  line-height: 1.5;
  font-weight: 400;
  color: #0f172a;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const MessageHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const MessageFooterRow = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const MessageAttachmentStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const MessageMetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #eef2f7;
  color: #475569;
  border: 1px solid #dbe3f1;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
`;

const MessageMetaSymbol = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
`;

const MessageTranslationBlock = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? '180px' : '0px')};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  margin-top: ${({ $expanded }) => ($expanded ? '12px' : '0px')};
  padding: ${({ $expanded }) => ($expanded ? '10px 12px' : '0 12px')};
  background: ${({ $expanded }) => ($expanded ? '#f8fafc' : 'transparent')};
  border-radius: 12px;
  transition:
    max-height 220ms ease,
    opacity 180ms ease,
    margin-top 180ms ease,
    padding 180ms ease;
`;

const MessageTranslationText = styled.div`
  font-size: 12px;
  line-height: 1.55;
  color: #64748b;
  font-weight: 500;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  hi: 'Hindi',
  ta: 'Tamil',
  te: 'Telugu',
  kn: 'Kannada',
  ml: 'Malayalam',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  ja: 'Japanese',
  zh: 'Chinese',
  und: 'Auto',
};

const LANGUAGE_ALIASES: Record<string, string> = {
  english: 'en',
  en: 'en',
  hindi: 'hi',
  hi: 'hi',
  tamil: 'ta',
  ta: 'ta',
  spanish: 'es',
  es: 'es',
  french: 'fr',
  fr: 'fr',
  german: 'de',
  de: 'de',
  japanese: 'ja',
  ja: 'ja',
  chinese: 'zh',
  zh: 'zh',
};

const getLanguageLabel = (code?: string): string => {
  const normalized = normalizeLanguageCode(code);
  return LANGUAGE_LABELS[normalized] || (normalized ? normalized.toUpperCase() : 'Text');
};

const MessageImage = styled.img`
  display: block;
  max-width: min(320px, 100%);
  max-height: 240px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  object-fit: cover;
`;

const MessageAudio = styled.audio`
  display: block;
  width: min(320px, 100%);
  max-width: 100%;
`;

const MessageAudioFallback = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${ACCENT_ORANGE_DARK};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const MessageDocument = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  color: ${ACCENT_ORANGE_TEXT};
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;

  &:hover {
    background: ${ACCENT_ORANGE_SOFT};
    border-color: ${ACCENT_ORANGE_BORDER};
  }
`;

const InputBar = styled.div`
  padding: 10px 12px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
  align-items: flex-end;
  background: #f8fafc;
  flex-shrink: 0;

  @media (max-width: 620px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ComposerShell = styled.div`
  flex: 1;
  background: #ffffff;
  border: 1px solid #dbe3f1;
  border-radius: 12px;
  padding: 8px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
  min-width: 0;
`;

const ComposerTopRow = styled.div<{ $mode: MessagingMode }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  flex-wrap: ${({ $mode }) => ($mode === 'widget' ? 'nowrap' : 'wrap')};
  overflow-x: auto;
`;


const AttachmentButton = styled.button<{ $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: ${({ $compact }) => ($compact ? '32px' : '28px')};
  min-width: ${({ $compact }) => ($compact ? '32px' : '0')};
  padding: ${({ $compact }) => ($compact ? '0' : '0 8px')};
  border: 1px solid #dbe3f1;
  background: #fffaf5;
  color: ${ACCENT_ORANGE_DARK};
  border-radius: 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  flex: 0 0 auto;

  &:hover {
    background: ${ACCENT_ORANGE_SOFT};
    border-color: ${ACCENT_ORANGE_BORDER};
  }

  @media (max-width: 1180px) {
    min-width: 28px;
    justify-content: center;
    padding: 0 6px;
  }
`;

const AttachmentLabel = styled.span<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'inline')};

  @media (max-width: 1180px) {
    display: none;
  }
`;

const AttachmentChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  border: 1px solid ${ACCENT_ORANGE_BORDER};
  background: ${ACCENT_ORANGE_SOFT};
  color: ${ACCENT_ORANGE_TEXT};
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 12px;
  min-width: 0;
`;

const AttachmentName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Composer = styled.textarea<{ $mode: MessagingMode }>`
  min-height: ${({ $mode }) => ($mode === 'widget' ? '52px' : '58px')};
  flex: 1;
  width: 100%;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  background: transparent;
  &:focus {
    outline: none;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const InlineStatus = styled.div`
  grid-column: 1 / -1;
  font-size: 12px;
  color: #6b7280;
`;

const StatusPill = styled.span<{ $ok?: boolean; $compact?: boolean }>`
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  color: ${(props) => (props.$ok ? '#166534' : '#92400e')};
  background: ${(props) => (props.$ok ? '#dcfce7' : '#fef3c7')};

  @media (max-width: 1180px) {
    display: ${(props) => (props.$compact ? 'inline-flex' : 'none')};
  }
`;

type LiveMessage = {
  id?: number | string;
  thread_id: number;
  sender_contact_id: number;
  sender_name?: string;
  message_text: string;
  text?: string;
  translated_text?: string;
  original_text?: string;
  source_language?: string;
  target_language?: string;
  message_type?: string;
  created_at?: string;
};

type AttachmentType = 'image' | 'audio' | 'document';

type ParsedAttachment = {
  kind: AttachmentType;
  url: string;
  fileName?: string;
  caption?: string;
};

type ContactOption = {
  id: number;
  name: string;
  role?: string;
  team?: string;
  email?: string;
  preferred_language?: string;
};

type AvailableChat = {
  thread_id: number;
  thread_title: string;
  thread_type?: string;
  is_direct?: boolean;
  participant_count?: number;
  participant_names?: string | null;
  target_contact?: {
    id: number;
    name: string;
    preferred_language?: string;
  } | null;
  last_message?: string | null;
  last_message_at?: string | null;
};

type AvailableChatsResponse = {
  threads?: AvailableChat[];
  current_contact?: {
    id: number;
    name?: string;
    email?: string;
  } | null;
};

type ActiveContact = {
  id: number;
  name: string;
  preferred_language?: string;
};

type SenderIdentity = {
  id: number;
  name: string;
  email?: string;
};

export interface MessagingPlatformProps {
  initialThreadId: number;
  initialThreadTitle: string;
  senderContactId: number;
  senderName: string;
  senderEmail?: string;
  initialTargetContactId?: number;
  initialTargetContactName?: string;
  initialDraftText?: string;
  preferredLanguage?: string;
  mode?: MessagingMode;
  onMinimizeConversation?: (payload: {
    threadId: number;
    threadTitle: string;
    targetContactId?: number;
    targetContactName?: string;
  }) => void;
  onCloseConversation?: () => void;
  onOpenHub?: () => void;
  onPopoutConversation?: (payload: {
    threadId: number;
    threadTitle: string;
    targetContactId?: number;
    targetContactName?: string;
  }) => void;
  onConversationChange?: (payload: {
    threadId: number;
    threadTitle: string;
    targetContactId?: number;
    targetContactName?: string;
  }) => void;
  onMessageActivity?: (payload: {
    threadId: number;
    messageId?: number | string;
    own: boolean;
    direct: boolean;
  }) => void;
  onConnectionStatusChange?: (payload: {
    threadId: number;
    connected: boolean;
  }) => void;
}

const getApiRoot = () => API_BASE_URL.replace(/\/graphql\/?$/, '');

const ATTACHMENT_TOKEN_RE = /^\[\[attachment\|(image|audio|document)\|([^|\]]+)\|([^\]]*)\]\](?:\s*(.*))?$/i;
const RECENT_CONVERSATION_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

const firstNonEmpty = (...values: Array<unknown>): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return '';
};

const parseAttachmentMessage = (text: string): ParsedAttachment | null => {
  const match = ATTACHMENT_TOKEN_RE.exec((text || '').trim());
  if (!match) {
    return null;
  }
  const [, kindRaw, url, fileName, caption] = match;
  const kind = kindRaw.toLowerCase() as AttachmentType;
  if (!url) {
    return null;
  }
  return {
    kind,
    url,
    fileName: fileName || undefined,
    caption: caption || undefined,
  };
};

const attachmentFallbackSummary = (text?: string): string => {
  const raw = String(text || '').trim();
  if (!raw.toLowerCase().startsWith('[[attachment|')) {
    return '';
  }

  const kindMatch = raw.match(/^\[\[attachment\|([^|\]]+)/i);
  const kind = String(kindMatch?.[1] || '').toLowerCase();
  const tokenEndIndex = raw.indexOf(']]');
  const caption = tokenEndIndex >= 0 ? raw.slice(tokenEndIndex + 2).trim() : '';
  if (caption) {
    return caption;
  }

  if (kind === 'image') {
    return 'Image shared';
  }
  if (kind === 'audio') {
    return 'Audio note shared';
  }
  if (kind === 'document') {
    return 'Document shared';
  }
  return 'Attachment shared';
};

const isWithinRecentWindow = (createdAt?: string): boolean => {
  if (!createdAt) {
    return true;
  }
  const ts = new Date(createdAt).getTime();
  if (Number.isNaN(ts)) {
    return true;
  }
  return Date.now() - ts <= RECENT_CONVERSATION_WINDOW_MS;
};

const normalizeLanguageCode = (code?: string): string => {
  const base = String(code || '')
  .trim()
  .toLowerCase()
  .replace('_', '-')
  .split('-')[0];
  return LANGUAGE_ALIASES[base] || base;
};

const normalizeTriggerText = (text?: string): string => String(text || '')
  .trim()
  .toLowerCase()
  .replace(/[.!?,'"“”‘’()\[\]{}:;\-_/\\]/g, '')
  .replace(/\s+/g, ' ');

const UNIVERSAL_PHRASES = new Set([
  'hi',
  'hello',
  'hey',
  'ok',
  'okay',
  'thanks',
  'thank you',
  'yes',
  'no',
  'done',
  'sure',
  'fine',
]);

const isUniversalPhrase = (text?: string): boolean => {
  const normalized = normalizeTriggerText(text);
  return Boolean(normalized) && (UNIVERSAL_PHRASES.has(normalized) || normalized.length <= 3);
};

const formatMessageTime = (createdAt?: string): string => {
  if (!createdAt) {
    return '';
  }
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatMessageDateLabel = (createdAt?: string): string => {
  if (!createdAt) {
    return '';
  }
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const absoluteDate = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

  if (sameDay(date, today)) {
    return `Today • ${absoluteDate}`;
  }
  if (sameDay(date, yesterday)) {
    return `Yesterday • ${absoluteDate}`;
  }
  return absoluteDate;
};

const toEpoch = (value?: string): number => {
  if (!value) {
    return 0;
  }
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

const playIncomingNotificationTone = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return;
  }
  const audioContext = new AudioContextCtor();
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.value = 920;
  gain.gain.value = 0.025;
  oscillator.connect(gain);
  gain.connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + 0.14);
  oscillator.onended = () => {
    void audioContext.close();
  };
};

export const MessagingPlatform: React.FC<MessagingPlatformProps> = ({
  initialThreadId,
  initialThreadTitle,
  senderContactId: providedSenderContactId,
  senderName,
  senderEmail,
  initialTargetContactId,
  initialTargetContactName,
  initialDraftText,
  preferredLanguage = 'en',
  mode = 'page',
  onMinimizeConversation,
  onCloseConversation,
  onOpenHub,
  onPopoutConversation,
  onConversationChange,
  onMessageActivity,
  onConnectionStatusChange,
}) => {
  const [leftNav, setLeftNav] = useState<'notifications' | 'messages' | 'groups'>('messages');
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [composeSearch, setComposeSearch] = useState('');
  const [composeParticipantIds, setComposeParticipantIds] = useState<number[]>([]);
  const [composeGroupTitle, setComposeGroupTitle] = useState('');
  const [hiddenThreadIds, setHiddenThreadIds] = useState<Set<number>>(new Set());
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(initialThreadId);
  const [activeThreadTitle, setActiveThreadTitle] = useState(initialThreadTitle);
  const [activeContact, setActiveContact] = useState<ActiveContact | null>(
    initialTargetContactId && initialTargetContactName
      ? { id: initialTargetContactId, name: initialTargetContactName }
      : null
  );
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [availableChats, setAvailableChats] = useState<AvailableChat[]>([]);
  const [availableChatsLoading, setAvailableChatsLoading] = useState(false);
  const [senderIdentity, setSenderIdentity] = useState<SenderIdentity | null>(null);
  const [selectingContactId, setSelectingContactId] = useState<number | null>(null);
  const [messages, setMessages] = useState<LiveMessage[]>([]);
  const messageIdsRef = useRef<Set<number | string>>(new Set());
  const historyLoadedRef = useRef(false);
  const pendingMessagesRef = useRef<LiveMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [identityMismatchWarning, setIdentityMismatchWarning] = useState<string | null>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<{ file: File; kind: AttachmentType; caption?: string } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [unreadByThread, setUnreadByThread] = useState<Record<number, number>>({});
  const wsRef = useRef<WebSocket | null>(null);
  const imageFileRef = useRef<HTMLInputElement | null>(null);
  const documentFileRef = useRef<HTMLInputElement | null>(null);
  const messagesAreaRef = useRef<HTMLDivElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaMimeTypeRef = useRef<string>('audio/webm');
  const recordedChunksRef = useRef<Blob[]>([]);
  const speechRecognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const recordedTranscriptRef = useRef('');
  const pendingOutgoingRef = useRef<string[]>([]);
  const latestThreadTimestampRef = useRef<number>(0);
  const chatsLoadedRef = useRef(false);
  const threadSeenAtRef = useRef<Record<number, number>>({});
  const senderNameRef = useRef(senderName);
  const preferredLanguageRef = useRef(preferredLanguage);
  const onMessageActivityRef = useRef(onMessageActivity);
  const onConnectionStatusChangeRef = useRef(onConnectionStatusChange);
  const onConversationChangeRef = useRef(onConversationChange);

  const apiRoot = useMemo(() => getApiRoot(), []);
  const fallbackSenderContactId = useMemo(() => {
    const parsed = Number(providedSenderContactId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [providedSenderContactId]);
  const activeConversation = useMemo(() => ({
    threadId: activeThreadId,
    threadTitle: activeThreadTitle,
    targetContactId: activeContact?.id,
    targetContactName: activeContact?.name,
  }), [activeContact?.id, activeContact?.name, activeThreadId, activeThreadTitle]);
  const resolvedSenderContactId = senderIdentity?.id ?? fallbackSenderContactId;
  const senderIdentityReady = Number.isFinite(resolvedSenderContactId) && resolvedSenderContactId > 0;
  const activeThreadIdRef = useRef(activeThreadId);
  const wasComposeOpenRef = useRef(isComposeOpen);
  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    // Reset compose filters exactly when the compose panel is opened.
    if (!wasComposeOpenRef.current && isComposeOpen) {
      setComposeSearch('');
      setComposeParticipantIds([]);
      setComposeGroupTitle('');
    }
    wasComposeOpenRef.current = isComposeOpen;
  }, [isComposeOpen]);
  useEffect(() => {
    const threadKey = Number(activeThreadId);
    if (!threadKey || Number.isNaN(threadKey)) {
      return;
    }
    const currentSeen = threadSeenAtRef.current[threadKey] || 0;
    threadSeenAtRef.current = { ...threadSeenAtRef.current, [threadKey]: Math.max(currentSeen, Date.now()) };
    setUnreadByThread((prev) => ({ ...prev, [threadKey]: 0 }));
  }, [activeThreadId]);
  const visibleContacts = useMemo(
    () => contacts.filter((contact) => Number(contact.id) !== Number(resolvedSenderContactId)),
    [contacts, resolvedSenderContactId]
  );

  const directChats = useMemo(() => {
    const directOnly = availableChats.filter((chat) => Boolean(chat.is_direct));
    const deduped = new Map<string, AvailableChat>();

    const toTimestamp = (value?: string | null): number => {
      if (!value) return 0;
      const ts = new Date(value).getTime();
      return Number.isNaN(ts) ? 0 : ts;
    };

    for (const chat of directOnly) {
      const pairKey = chat.target_contact?.id !== undefined && chat.target_contact?.id !== null
        ? `target:${chat.target_contact.id}`
        : `title:${(chat.target_contact?.name || chat.thread_title || '').trim().toLowerCase()}`;
      const current = deduped.get(pairKey);
      if (!current) {
        deduped.set(pairKey, chat);
        continue;
      }
      if (toTimestamp(chat.last_message_at) >= toTimestamp(current.last_message_at)) {
        deduped.set(pairKey, chat);
      }
    }

    return Array.from(deduped.values()).sort(
      (a, b) => toTimestamp(b.last_message_at) - toTimestamp(a.last_message_at)
    );
  }, [availableChats]);

  const groupChats = useMemo(
    () => availableChats.filter((chat) => !chat.is_direct),
    [availableChats]
  );
  const recentNotifications = useMemo(
    () => availableChats
      .filter((chat) => Boolean(chat.last_message_at))
      .sort((a, b) => toEpoch(b.last_message_at || undefined) - toEpoch(a.last_message_at || undefined))
      .slice(0, 12),
    [availableChats]
  );
  const activeChatRecord = useMemo(
    () => availableChats.find((chat) => Number(chat.thread_id) === Number(activeThreadId)) || null,
    [availableChats, activeThreadId]
  );
  const middlePaneItems = useMemo(() => {
    if (leftNav === 'groups') {
      return groupChats.filter((chat) => !hiddenThreadIds.has(Number(chat.thread_id)));
    }
    if (leftNav === 'notifications') {
      return recentNotifications.filter((chat) => !hiddenThreadIds.has(Number(chat.thread_id)));
    }
    return directChats.filter((chat) => !hiddenThreadIds.has(Number(chat.thread_id)));
  }, [directChats, groupChats, hiddenThreadIds, leftNav, recentNotifications]);
  const filteredComposeContacts = useMemo(() => {
    const normalized = composeSearch.trim().toLowerCase();
    if (!normalized) {
      return visibleContacts;
    }
    return visibleContacts.filter((contact) => contact.name.toLowerCase().includes(normalized));
  }, [composeSearch, visibleContacts]);
  const selectedComposeContacts = useMemo(
    () => visibleContacts.filter((contact) => composeParticipantIds.includes(contact.id)),
    [composeParticipantIds, visibleContacts]
  );
  const composeContactsForList = useMemo(() => {
    const selected = visibleContacts.filter((contact) => composeParticipantIds.includes(contact.id));
    const unselectedFiltered = filteredComposeContacts.filter((contact) => !composeParticipantIds.includes(contact.id));
    return [...selected, ...unselectedFiltered];
  }, [composeParticipantIds, filteredComposeContacts, visibleContacts]);
  const inferredReceiverLanguage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (Number(msg.sender_contact_id) === Number(resolvedSenderContactId)) {
        continue;
      }
      const source = normalizeLanguageCode(msg.source_language);
      if (source && source !== 'und') {
        return source;
      }
    }
    return '';
  }, [messages, resolvedSenderContactId]);
  const activeContactRecord = useMemo(
    () => contacts.find((contact) => Number(contact.id) === Number(activeContact?.id)) || null,
    [contacts, activeContact?.id]
  );
  const explicitReceiverLanguage = normalizeLanguageCode(
    activeChatRecord?.target_contact?.preferred_language
      || activeContact?.preferred_language
      || activeContactRecord?.preferred_language
      || ''
  );
  const resolvedReceiverLanguage = explicitReceiverLanguage || inferredReceiverLanguage;
  const senderLanguageLabel = getLanguageLabel(preferredLanguage);
  const receiverLanguageLabel = resolvedReceiverLanguage ? getLanguageLabel(resolvedReceiverLanguage) : 'Auto';
  const normalizedPreferredLanguage = normalizeLanguageCode(preferredLanguage) || 'en';

  const deriveChatDisplayName = (chat: AvailableChat): string => {
    if (chat.is_direct) {
      if (chat.target_contact?.name) {
        return chat.target_contact.name;
      }
      const rawTitle = String(chat.thread_title || '').replace(/^Direct:\s*/i, '').trim();
      if (rawTitle.includes('<>')) {
        const parts = rawTitle.split('<>').map((p) => p.trim()).filter(Boolean);
        const currentNames = [senderIdentity?.name, senderName].map((n) => String(n || '').trim().toLowerCase()).filter(Boolean);
        const other = parts.find((p) => !currentNames.includes(p.toLowerCase()));
        return other || parts[0] || 'Conversation';
      }
      return rawTitle || 'Conversation';
    }
    return chat.thread_title || `Thread #${chat.thread_id}`;
  };

  const activeChatDisplayTitle = useMemo(() => {
    if (activeChatRecord) {
      return deriveChatDisplayName(activeChatRecord);
    }
    if (activeContact?.name) {
      return activeContact.name;
    }
    return String(activeThreadTitle || '').replace(/^Direct:\s*/i, '').trim() || 'Conversation';
  }, [activeChatRecord, activeContact?.name, activeThreadTitle, senderIdentity?.name, senderName]);

  const renderThreadPreviewText = (chat: AvailableChat): string => {
    const attachmentSummary = attachmentFallbackSummary(chat.last_message || undefined);
    if (attachmentSummary) {
      return attachmentSummary;
    }
    if (chat.last_message && chat.last_message.trim()) {
      return chat.last_message;
    }
    if (chat.is_direct) {
      return 'Personal chat';
    }
    return `${chat.participant_count || 0} members`;
  };

  useEffect(() => {
    if (mode === 'page' && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [mode]);

  useEffect(() => {
    setActiveThreadId(initialThreadId);
    setActiveThreadTitle(initialThreadTitle);
    if (initialTargetContactId !== undefined && initialTargetContactId !== null && initialTargetContactName) {
      setActiveContact({ id: initialTargetContactId, name: initialTargetContactName });
    } else {
      setActiveContact(null);
    }
    if (initialDraftText) {
      setMessageInput(initialDraftText);
    }
  }, [initialThreadId, initialThreadTitle, initialTargetContactId, initialTargetContactName, initialDraftText]);

  useEffect(() => {
    senderNameRef.current = senderName;
  }, [senderName]);

  useEffect(() => {
    preferredLanguageRef.current = preferredLanguage;
  }, [preferredLanguage]);

  useEffect(() => {
    onMessageActivityRef.current = onMessageActivity;
  }, [onMessageActivity]);

  useEffect(() => {
    onConnectionStatusChangeRef.current = onConnectionStatusChange;
  }, [onConnectionStatusChange]);

  useEffect(() => {
    onConversationChangeRef.current = onConversationChange;
  }, [onConversationChange]);

  useEffect(() => {
    // onConversationChange is intentionally excluded from the deps below —
    // most call sites pass a fresh inline arrow on every parent render, and
    // depending on it directly here caused an infinite update loop (parent
    // re-render -> new callback identity -> this effect fires -> parent
    // state update -> re-render...). The ref keeps the latest callback
    // without retriggering this effect on identity alone.
    onConversationChangeRef.current?.(activeConversation);
  }, [activeConversation]);

  useEffect(() => {
    let cancelled = false;

    const resolveSenderIdentity = async () => {
      try {
        const res = await fetch(`${apiRoot}/api/v1/messaging/resolve-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: senderName,
            email: senderEmail,
            role: 'Internal User',
            team: 'Messaging',
          }),
        });
        if (!res.ok) {
          throw new Error('Failed to resolve messaging identity');
        }
        const payload = await res.json();
        if (!cancelled && payload?.contact?.id !== undefined) {
          setSenderIdentity({
            id: Number(payload.contact.id),
            name: payload.contact.name || senderName,
            email: payload.contact.email || senderEmail,
          });
          setIdentityMismatchWarning(null);
        }
      } catch {
        if (!cancelled) {
          if (fallbackSenderContactId > 0) {
            setSenderIdentity({
              id: fallbackSenderContactId,
              name: senderName,
              email: senderEmail,
            });
            setIdentityMismatchWarning('Using local identity fallback. Live contacts/threads are still available.');
            setError(null);
          } else {
            setError('Could not resolve your messaging identity.');
          }
        }
      }
    };

    resolveSenderIdentity();
    return () => {
      cancelled = true;
    };
  }, [apiRoot, fallbackSenderContactId, senderEmail, senderName]);

  useEffect(() => {
    let cancelled = false;

    const loadContacts = async () => {
      if (!senderIdentityReady) {
        return;
      }
      setContactsLoading(true);
      try {
        const url = `${apiRoot}/api/v1/messaging/contacts?exclude_contact_id=${encodeURIComponent(String(resolvedSenderContactId))}`;
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error('Failed to load contacts');
        }
        const payload = await res.json();
        if (!cancelled && Array.isArray(payload?.contacts)) {
          setContacts(payload.contacts as ContactOption[]);
        }
      } catch {
        if (!cancelled) {
          setError('Could not load contact list.');
        }
      } finally {
        if (!cancelled) {
          setContactsLoading(false);
        }
      }
    };

    loadContacts();
    return () => {
      cancelled = true;
    };
}, [apiRoot, resolvedSenderContactId, senderIdentityReady]);

  useEffect(() => {
    let cancelled = false;

    const toTimestamp = (value?: string | null): number => {
      if (!value) return 0;
      const ts = new Date(value).getTime();
      return Number.isNaN(ts) ? 0 : ts;
    };

    const loadAvailableChats = async () => {
      if (!senderIdentityReady) {
        return;
      }
      if (!chatsLoadedRef.current) {
        setAvailableChatsLoading(true);
      }
      try {
        const url = `${apiRoot}/api/v1/messaging/threads?contact_id=${encodeURIComponent(String(resolvedSenderContactId))}&limit=50&offset=0`;
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error('Failed to load available chats');
        }
        const payload = await res.json() as AvailableChatsResponse;
        if (!cancelled && Array.isArray(payload?.threads)) {
          const fetchedThreads = payload.threads as AvailableChat[];

          const backendContact = payload.current_contact;
          const senderEmailKey = senderEmail?.trim().toLowerCase();
          const backendEmailKey = backendContact?.email?.trim().toLowerCase();
          const mismatch = Boolean(senderEmailKey && backendEmailKey && senderEmailKey !== backendEmailKey);

          if (mismatch) {
            setIdentityMismatchWarning('Messaging identity mismatch detected. Please re-login to sync contact identity.');
            setAvailableChats(fetchedThreads);
          } else {
            setIdentityMismatchWarning(null);
            setAvailableChats(fetchedThreads);
            chatsLoadedRef.current = true;

            const nextUnread: Record<number, number> = {};
            let incomingDetected = false;
            fetchedThreads.forEach((thread) => {
              const threadKey = Number(thread.thread_id);
              const lastTs = toTimestamp(thread.last_message_at || null);
              if (lastTs <= 0) {
                return;
              }
              const seenTs = threadSeenAtRef.current[threadKey] || 0;
              if (threadKey === Number(activeThreadIdRef.current)) {
                threadSeenAtRef.current = { ...threadSeenAtRef.current, [threadKey]: Math.max(seenTs, lastTs) };
                return;
              }
              if (lastTs > seenTs) {
                nextUnread[threadKey] = 1;
                incomingDetected = true;
              }
            });
            setUnreadByThread(nextUnread);

            const topThread = fetchedThreads[0];
            const topTimestamp = toTimestamp(topThread?.last_message_at || null);
            if (topTimestamp > latestThreadTimestampRef.current) {
              const hadPrevious = latestThreadTimestampRef.current > 0;
              latestThreadTimestampRef.current = topTimestamp;
              if (hadPrevious && incomingDetected) {
                playIncomingNotificationTone();
              }
            }

            const hasActive = fetchedThreads.some((chat) => Number(chat.thread_id) === Number(activeThreadIdRef.current));
            if (!hasActive && fetchedThreads.length > 0) {
              const first = fetchedThreads[0];
              setActiveThreadId(Number(first.thread_id));
              setActiveThreadTitle(first.thread_title || `Thread #${first.thread_id}`);
              if (first.is_direct && first.target_contact) {
                setActiveContact({
                  id: first.target_contact.id,
                  name: first.target_contact.name,
                  preferred_language: first.target_contact.preferred_language,
                });
              } else {
                setActiveContact(null);
              }
            }
          }
        }
      } catch {
        if (!cancelled) {
          setError('Could not load available chats.');
        }
      } finally {
        if (!cancelled) {
          setAvailableChatsLoading(false);
        }
      }
    };

    loadAvailableChats();

    const interval = setInterval(() => {
      void loadAvailableChats();
    }, 6000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [apiRoot, resolvedSenderContactId, senderIdentityReady, senderEmail]);

  useEffect(() => {
    let cancelled = false;
    messageIdsRef.current.clear();
    historyLoadedRef.current = false;
    pendingMessagesRef.current = [];
    const loadHistory = async () => {
      try {
        const url = `${apiRoot}/api/v1/messaging/history?thread_id=${encodeURIComponent(activeThreadId)}&lang=${encodeURIComponent(normalizedPreferredLanguage)}&limit=200`;
        const res = await fetch(url);
        if (!res.ok) return;
        const payload = await res.json();
        if (!cancelled && Array.isArray(payload?.messages)) {
          const loaded = (payload.messages as LiveMessage[]).filter((msg) => isWithinRecentWindow(msg.created_at));
          loaded.forEach((msg) => {
            if (msg.id) messageIdsRef.current.add(msg.id);
          });
          const merged = [...loaded];
          pendingMessagesRef.current.forEach((msg) => {
            if (!isWithinRecentWindow(msg.created_at)) {
              return;
            }
            if (msg.id && messageIdsRef.current.has(msg.id)) {
              return;
            }
            if (msg.id) {
              messageIdsRef.current.add(msg.id);
            }
            merged.push(msg);
          });
          setMessages(merged);
          pendingMessagesRef.current = [];
          historyLoadedRef.current = true;
        }
      } catch {
        // non-fatal
        historyLoadedRef.current = true;
      }
    };
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [apiRoot, activeThreadId, normalizedPreferredLanguage]);

  useEffect(() => {
    if (!senderIdentityReady) {
      return;
    }
    let closedByCleanup = false;
    let reconnectAttempts = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      const wsUrl = `${apiRoot.replace(/^http/, 'ws')}/ws/messaging?thread_id=${encodeURIComponent(String(activeThreadId))}&contact_id=${encodeURIComponent(String(resolvedSenderContactId))}&contact_name=${encodeURIComponent(senderNameRef.current)}&lang=${encodeURIComponent(normalizeLanguageCode(preferredLanguageRef.current) || 'en')}`;
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        reconnectAttempts = 0;
        setConnected(true);
        setIsSending(false);
        setError(null);
        onConnectionStatusChangeRef.current?.({ threadId: activeThreadId, connected: true });
        while (pendingOutgoingRef.current.length > 0) {
          const queued = pendingOutgoingRef.current.shift();
          if (!queued) {
            continue;
          }
          socket.send(JSON.stringify({ text: queued }));
        }
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === 'chat') {
            if (!isWithinRecentWindow(payload.created_at)) {
              return;
            }
            const msgId = payload.id;
            const own = Number(payload.sender_contact_id) === Number(resolvedSenderContactId);
            if (msgId && messageIdsRef.current.has(msgId)) {
              return; // Already have this message, skip duplicate
            }
            const isDirect = Number(payload.sender_contact_id) !== Number(resolvedSenderContactId)
              && activeContact?.id !== undefined
              && activeContact?.id !== null;
            onMessageActivityRef.current?.({ threadId: activeThreadId, messageId: msgId, own, direct: isDirect });
            if (!own && mode !== 'widget') {
              playIncomingNotificationTone();
            }
            if (!historyLoadedRef.current) {
              pendingMessagesRef.current.push(payload);
              return;
            }
            if (msgId) messageIdsRef.current.add(msgId);
            setMessages((prev) => [...prev, payload]);
          }
        } catch {
          // Ignore malformed payloads
        }
      };

      socket.onerror = () => {
        setError('Live connection interrupted. Reconnecting...');
      };

      socket.onclose = () => {
        setConnected(false);
        onConnectionStatusChangeRef.current?.({ threadId: activeThreadId, connected: false });
        if (closedByCleanup) {
          return;
        }
        reconnectAttempts += 1;
        const delay = Math.min(1000 * 2 ** Math.min(reconnectAttempts, 4), 10000);
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closedByCleanup = true;
      onConnectionStatusChangeRef.current?.({ threadId: activeThreadId, connected: false });
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [apiRoot, activeThreadId, resolvedSenderContactId, activeContact?.id, senderIdentityReady]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const area = messagesAreaRef.current;
      if (!area) {
        return;
      }
      area.scrollTop = area.scrollHeight;
    }, 40);
    return () => clearTimeout(timer);
  }, [messages.length]);

  const openAvailableChat = (chat: AvailableChat) => {
    const threadKey = Number(chat.thread_id);
    const seenTs = toEpoch(chat.last_message_at || undefined) || Date.now();
    threadSeenAtRef.current = { ...threadSeenAtRef.current, [threadKey]: seenTs };
    setUnreadByThread((prev) => ({ ...prev, [threadKey]: 0 }));
    setActiveThreadId(Number(chat.thread_id));
    setActiveThreadTitle(deriveChatDisplayName(chat));
    if (chat.is_direct && chat.target_contact) {
      setActiveContact({
        id: chat.target_contact.id,
        name: chat.target_contact.name,
        preferred_language: chat.target_contact.preferred_language,
      });
      return;
    }
    setActiveContact(null);
  };

  const selectContactConversation = async (contact: ContactOption) => {
    if (contact?.id === undefined || contact?.id === null || contact.id === resolvedSenderContactId) {
      return;
    }
    if (!senderIdentityReady) {
      setError('Resolving your messaging identity. Please try again in a moment.');
      return;
    }
    setSelectingContactId(contact.id);
    try {
      const res = await fetch(`${apiRoot}/api/v1/messaging/direct-thread`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_contact_id: resolvedSenderContactId,
          sender_name: senderName,
          target_contact_id: contact.id,
          preferred_language: preferredLanguage,
        }),
      });
      if (!res.ok) {
        throw new Error('Failed to open conversation');
      }
      const payload = await res.json();
      if (payload?.thread_id) {
        setActiveThreadId(Number(payload.thread_id));
        setActiveThreadTitle(contact.name);
        setActiveContact({ id: contact.id, name: contact.name, preferred_language: contact.preferred_language });
        setAvailableChats((prev) => {
          const existing = prev.find((chat) => Number(chat.thread_id) === Number(payload.thread_id));
          if (existing) {
            return prev;
          }
          return [
            {
              thread_id: Number(payload.thread_id),
              thread_title: contact.name,
              thread_type: 'general',
              is_direct: true,
              target_contact: { id: contact.id, name: contact.name },
            },
            ...prev,
          ];
        });
      }
    } catch {
      setError('Could not open conversation. Please try again.');
    } finally {
      setSelectingContactId(null);
    }
  };

  const toggleComposeParticipant = (contactId: number) => {
    setComposeParticipantIds((prev) => {
      if (prev.includes(contactId)) {
        return prev.filter((id) => id !== contactId);
      }
      return [...prev, contactId];
    });
  };

  const removeChatFromActiveScreen = (threadId: number) => {
    setHiddenThreadIds((prev) => {
      const next = new Set(prev);
      next.add(Number(threadId));
      return next;
    });

    if (Number(activeThreadIdRef.current) === Number(threadId)) {
      const remaining = availableChats
        .filter((chat) => Number(chat.thread_id) !== Number(threadId))
        .filter((chat) => !hiddenThreadIds.has(Number(chat.thread_id)));
      if (remaining.length > 0) {
        openAvailableChat(remaining[0]);
      } else {
        setIsComposeOpen(true);
      }
    }
  };

  const quickStartContactByDoubleClick = async (contact: ContactOption) => {
    setComposeParticipantIds([contact.id]);
    await selectContactConversation(contact);
    setIsComposeOpen(false);
    setComposeParticipantIds([]);
    setComposeSearch('');
    setComposeGroupTitle('');
  };

  const startConversationFromCompose = async () => {
    if (composeParticipantIds.length === 0) {
      setError('Select at least one contact to start a conversation.');
      return;
    }

    if (composeParticipantIds.length === 1) {
      const single = visibleContacts.find((contact) => Number(contact.id) === Number(composeParticipantIds[0]));
      if (!single) {
        setError('Selected contact no longer exists. Refresh and try again.');
        return;
      }
      await selectContactConversation(single);
      setIsComposeOpen(false);
      setComposeParticipantIds([]);
      setComposeSearch('');
      setComposeGroupTitle('');
      return;
    }

    await createGroupConversation(composeParticipantIds, composeGroupTitle);
    setIsComposeOpen(false);
    setComposeParticipantIds([]);
    setComposeSearch('');
    setComposeGroupTitle('');
  };

  const createGroupConversation = async (participantIds: number[], title?: string) => {
    if (!senderIdentityReady) {
      setError('Resolving your messaging identity. Please try again in a moment.');
      return;
    }
    if (participantIds.length < 2) {
      setError('Select at least 2 participants to create a group thread.');
      return;
    }

    setIsCreatingGroup(true);
    try {
      const res = await fetch(`${apiRoot}/api/v1/messaging/group-thread`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_contact_id: resolvedSenderContactId,
          sender_name: senderName,
          participant_contact_ids: participantIds,
          thread_title: title?.trim() || undefined,
          preferred_language: preferredLanguage,
        }),
      });

      if (!res.ok) {
        const failure = await res.json().catch(() => ({}));
        throw new Error(String(failure?.detail || 'Failed to create group thread'));
      }

      const payload = await res.json();
      const nextThreadId = Number(payload?.thread_id || 0);
      if (!nextThreadId) {
        throw new Error('Invalid group thread response');
      }

      const nextTitle = String(payload?.thread_title || title?.trim() || `Group Thread #${nextThreadId}`);
      setActiveThreadId(nextThreadId);
      setActiveThreadTitle(nextTitle);
      setActiveContact(null);
      setLeftNav('groups');

      setAvailableChats((prev) => {
        const exists = prev.some((chat) => Number(chat.thread_id) === nextThreadId);
        if (exists) {
          return prev;
        }
        return [
          {
            thread_id: nextThreadId,
            thread_title: nextTitle,
            thread_type: 'general',
            is_direct: false,
            participant_count: Number(payload?.participant_count || participantIds.length + 1),
          },
          ...prev,
        ];
      });
      setError(null);
    } catch (createError) {
      setError((createError as Error)?.message || 'Could not create group thread.');
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const uploadAttachment = async (file: File): Promise<{ url: string; fileName: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${apiRoot}/api/v1/messaging/upload`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      throw new Error('Attachment upload failed');
    }
    const payload = await res.json();
    const fileUrl = String(payload?.file_url || '');
    if (!fileUrl) {
      throw new Error('Upload did not return a file URL');
    }
    const absoluteUrl = fileUrl.startsWith('http') ? fileUrl : `${apiRoot}${fileUrl}`;
    return {
      url: absoluteUrl,
      fileName: String(payload?.file_name || file.name),
    };
  };

  const sendMessage = async () => {
    const text = messageInput.trim();
    if ((!text && !selectedAttachment) || isSending) {
      return;
    }

    let outgoing = text;
    setIsSending(true);

    if (selectedAttachment) {
      try {
        const uploaded = await uploadAttachment(selectedAttachment.file);
        const caption = text || selectedAttachment.caption || '';
        outgoing = `[[attachment|${selectedAttachment.kind}|${uploaded.url}|${uploaded.fileName}]]${caption ? ` ${caption}` : ''}`;
      } catch {
        setError('Could not upload attachment. Please try again.');
        setIsSending(false);
        return;
      }
    }

    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      pendingOutgoingRef.current.push(outgoing);
      setError('Connection is restoring. Message queued and will send automatically.');
      setMessageInput('');
      setSelectedAttachment(null);
      setIsSending(false);
      return;
    }

    socket.send(JSON.stringify({ text: outgoing }));
    setMessageInput('');
    setSelectedAttachment(null);
    setIsSending(false);
  };

  const toggleAudioRecording = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      return;
    }

    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Audio recording is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredMimeTypes = [
        'audio/mp4',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
      ];
      const chosenMime = preferredMimeTypes.find(
        (mime) => typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(mime)
      ) || 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType: chosenMime });
      mediaMimeTypeRef.current = chosenMime;
      const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      mediaStreamRef.current = stream;
      recordedChunksRef.current = [];
      recordedTranscriptRef.current = '';

      if (SpeechRecognitionCtor) {
        const recognition: BrowserSpeechRecognition = new SpeechRecognitionCtor();
        const langMap: Record<string, string> = {
          en: 'en-US',
          hi: 'hi-IN',
          es: 'es-ES',
          fr: 'fr-FR',
          ta: 'ta-IN',
          te: 'te-IN',
          kn: 'kn-IN',
          ml: 'ml-IN',
          de: 'de-DE',
          ja: 'ja-JP',
          zh: 'zh-CN',
        };
        const normalizedRecognitionLang = normalizeLanguageCode(preferredLanguageRef.current);
        recognition.lang = langMap[normalizedRecognitionLang] || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.onresult = (event: any) => {
          const results = event?.results;
          if (!results) {
            return;
          }
          let transcript = '';
          for (let i = event.resultIndex || 0; i < results.length; i += 1) {
            transcript += results[i]?.[0]?.transcript || '';
          }
          if (transcript.trim()) {
            recordedTranscriptRef.current = `${recordedTranscriptRef.current} ${transcript}`.trim();
          }
        };
        recognition.onerror = () => {
          // Silent fallback: audio message still sends even if transcript is unavailable.
        };
        recognition.onend = () => {
          // no-op; on stop we wait briefly for final result to flush into recordedTranscriptRef
        };
        recognition.start();
        speechRecognitionRef.current = recognition;
      }

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const chunks = recordedChunksRef.current;
        const recognition = speechRecognitionRef.current;
        if (recognition) {
          await new Promise<void>((resolve) => {
            let resolved = false;
            const finalize = () => {
              if (resolved) return;
              resolved = true;
              resolve();
            };
            const timer = setTimeout(finalize, 700);
            const prevOnEnd = recognition.onend;
            recognition.onend = (event: any) => {
              try {
                prevOnEnd?.(event);
              } catch {
                // ignore recognition callback chaining errors
              }
              clearTimeout(timer);
              finalize();
            };
            try {
              recognition.stop();
            } catch {
              clearTimeout(timer);
              finalize();
            }
          });
          speechRecognitionRef.current = null;
        }
        if (chunks.length > 0) {
          const mimeType = mediaMimeTypeRef.current || 'audio/webm';
          const ext = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm';
          const blob = new Blob(chunks, { type: mimeType });
          const file = new File([blob], `voice-note-${Date.now()}.${ext}`, { type: mimeType });
          const transcriptCaption = recordedTranscriptRef.current.trim();
          setSelectedAttachment({ file, kind: 'audio', caption: transcriptCaption || undefined });
        }
        recordedChunksRef.current = [];
        recordedTranscriptRef.current = '';
        mediaRecorderRef.current = null;
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        setIsRecording(false);
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setError(null);
    } catch {
      setError('Microphone permission denied or unavailable.');
      setIsRecording(false);
    }
  };

  useEffect(() => () => {
    speechRecognitionRef.current?.stop();
    mediaRecorderRef.current?.stop();
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const activeThreadInCurrentPane = middlePaneItems.some((chat) => Number(chat.thread_id) === Number(activeThreadId));
  const handlePickFromDropdown = (threadIdText: string) => {
    const threadIdValue = Number(threadIdText);
    if (!threadIdValue || Number.isNaN(threadIdValue)) {
      return;
    }
    const picked = middlePaneItems.find((chat) => Number(chat.thread_id) === threadIdValue);
    if (!picked) {
      return;
    }
    setIsComposeOpen(false);
    openAvailableChat(picked);
  };

  const showHubAction = mode !== 'page' && onOpenHub;
  const showMinimizeAction = Boolean(onMinimizeConversation);
  const showCloseAction = mode !== 'page' && onCloseConversation;
  const showPopoutAction = Boolean(onPopoutConversation);
  const compactWidgetMode = mode === 'widget';

  return (
    <PageShell>
      <MessagingContainer $mode={mode}>
        <TeamsShell $mode={mode}>
          {mode === 'widget' ? (
            <WidgetTopControls>
              <WidgetTopRow>
                <WidgetRailInline>
                  <RailButton type="button" $active={leftNav === 'notifications'} onClick={() => setLeftNav('notifications')} title="Notifications">
                    <Icon name="Inbox" size={16} />
                  </RailButton>
                  <RailButton type="button" $active={leftNav === 'messages'} onClick={() => setLeftNav('messages')} title="Messages">
                    <Icon name="Send" size={16} />
                  </RailButton>
                  <RailButton type="button" $active={leftNav === 'groups'} onClick={() => setLeftNav('groups')} title="Groups">
                    <Icon name="FileText" size={16} />
                  </RailButton>
                </WidgetRailInline>
                {leftNav !== 'notifications' && (
                  <IconButton
                    type="button"
                    title="New chat"
                    onClick={() => {
                      setIsComposeOpen(true);
                      setComposeSearch('');
                      setComposeParticipantIds([]);
                      setComposeGroupTitle('');
                    }}
                  >
                    <Icon name="UserPlus" size={14} />
                  </IconButton>
                )}
              </WidgetTopRow>
              <WidgetThreadSelect
                value={activeThreadInCurrentPane ? String(activeThreadId) : ''}
                onChange={(e) => handlePickFromDropdown(e.target.value)}
                aria-label="Select active chat"
              >
                <option value="">
                  {availableChatsLoading
                    ? 'Loading conversations...'
                    : leftNav === 'notifications'
                      ? 'Select notification'
                      : leftNav === 'groups'
                        ? 'Select active group'
                        : 'Select active chat'}
                </option>
                {middlePaneItems.map((chat) => (
                  <option key={`compact-${chat.thread_id}`} value={chat.thread_id}>
                    {deriveChatDisplayName(chat)}
                  </option>
                ))}
              </WidgetThreadSelect>
            </WidgetTopControls>
          ) : (
            <>
              <LeftRail>
                <RailButton type="button" $active={leftNav === 'notifications'} onClick={() => setLeftNav('notifications')} title="Notifications">
                  <Icon name="Inbox" size={16} />
                </RailButton>
                <RailButton type="button" $active={leftNav === 'messages'} onClick={() => setLeftNav('messages')} title="Messages">
                  <Icon name="Send" size={16} />
                </RailButton>
                <RailButton type="button" $active={leftNav === 'groups'} onClick={() => setLeftNav('groups')} title="Groups">
                  <Icon name="FileText" size={16} />
                </RailButton>
              </LeftRail>

              <ListPane>
                <ListHeader>
                  <ListTitle>
                    {leftNav === 'notifications' ? 'Notifications' : leftNav === 'groups' ? 'Groups' : 'Messages'}
                  </ListTitle>
                  {leftNav !== 'notifications' && (
                    <IconButton
                      type="button"
                      title="New chat"
                      onClick={() => {
                        setIsComposeOpen(true);
                        setComposeSearch('');
                        setComposeParticipantIds([]);
                        setComposeGroupTitle('');
                      }}
                    >
                      <Icon name="UserPlus" size={14} />
                    </IconButton>
                  )}
                </ListHeader>
                <ListBody>
                  {availableChatsLoading && <ListItemMeta>Loading conversations...</ListItemMeta>}
                  {!availableChatsLoading && middlePaneItems.length === 0 && (
                    <ListItemMeta>
                      {leftNav === 'notifications' ? 'No notifications yet.' : leftNav === 'groups' ? 'No active groups.' : 'No active messages.'}
                    </ListItemMeta>
                  )}
                  {middlePaneItems.map((chat) => {
                    const active = Number(chat.thread_id) === Number(activeThreadId);
                    return (
                      <ListItem
                        key={`pane-${chat.thread_id}`}
                        type="button"
                        $active={active}
                        onClick={() => {
                          setIsComposeOpen(false);
                          openAvailableChat(chat);
                        }}
                      >
                        <ListItemTop>
                          <ListItemTitle>{deriveChatDisplayName(chat)}</ListItemTitle>
                          <ListItemRight>
                            {(unreadByThread[Number(chat.thread_id)] || 0) > 0 ? (
                              <UnreadBadge title="Unread messages">
                                {unreadByThread[Number(chat.thread_id)] > 9 ? '9+' : unreadByThread[Number(chat.thread_id)]}
                              </UnreadBadge>
                            ) : null}
                            {leftNav !== 'notifications' && (
                              <ListItemRemoveButton
                                type="button"
                                title="Remove from active list"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeChatFromActiveScreen(Number(chat.thread_id));
                                }}
                              >
                                <Icon name="X" size={12} />
                              </ListItemRemoveButton>
                            )}
                          </ListItemRight>
                        </ListItemTop>
                        <ListItemMeta>
                          {renderThreadPreviewText(chat)}
                        </ListItemMeta>
                      </ListItem>
                    );
                  })}
                </ListBody>
              </ListPane>
            </>
          )}

          <ConversationPane>
            <PanelHeader $mode={mode}>
              <HeaderTopRow $mode={mode}>
                <ActiveChatTitle $mode={mode}>
                  {isComposeOpen
                    ? 'New Message'
                    : activeChatDisplayTitle}
                </ActiveChatTitle>
                <HeaderActions>
                  {showPopoutAction && (
                    <IconButton onClick={() => onPopoutConversation?.(activeConversation)} title="Open in new window">
                      <Icon name="Inbox" size={16} />
                    </IconButton>
                  )}
                  {showHubAction && (
                    <IconButton onClick={onOpenHub} title="Open inbox">
                      <Icon name="Inbox" size={16} />
                    </IconButton>
                  )}
                  {showMinimizeAction && (
                    <IconButton onClick={() => onMinimizeConversation?.(activeConversation)} title="Minimize">
                      <Icon name="Minus" size={16} />
                    </IconButton>
                  )}
                  {showCloseAction && (
                    <IconButton onClick={onCloseConversation} title="Close conversation">
                      <Icon name="X" size={16} />
                    </IconButton>
                  )}
                </HeaderActions>
              </HeaderTopRow>
              <HeaderMeta $mode={mode}>
                {compactWidgetMode ? (
                  <>
                    <CompactMetaBadge title={`My Language: ${senderLanguageLabel}`}>{senderLanguageLabel}</CompactMetaBadge>
                    <CompactMetaBadge title={`Receiving: ${receiverLanguageLabel || 'Auto-Detect'}`}>{receiverLanguageLabel || 'Auto'}</CompactMetaBadge>
                    <StatusPill title={connected ? 'Live' : 'Offline'} $ok={connected} $compact>{connected ? 'Live' : 'Off'}</StatusPill>
                  </>
                ) : (
                  <>
                    <LangBadge>My Language: {senderLanguageLabel}</LangBadge>
                    <LangBadge>Receiving: {receiverLanguageLabel || 'Auto-Detect'}</LangBadge>
                    <StatusPill $ok={connected}>{connected ? 'Live' : 'Offline'}</StatusPill>
                  </>
                )}
              </HeaderMeta>
              {!isComposeOpen && activeChatRecord && !activeChatRecord.is_direct && activeChatRecord.participant_names ? (
                <ActiveChatParticipants>
                  Participants: {activeChatRecord.participant_names}
                </ActiveChatParticipants>
              ) : null}
            </PanelHeader>

            {isComposeOpen ? (
              <ComposePanel>
                <ComposeHint>Select one contact for direct chat, or multiple contacts to start a group chat automatically.</ComposeHint>
                <ToBox>
                  <ToLabel>To</ToLabel>
                  <ToChipWrap>
                    {selectedComposeContacts.length === 0 ? (
                      <ListItemMeta>Add contacts from the list below</ListItemMeta>
                    ) : selectedComposeContacts.map((contact) => (
                      <ToChip key={`to-chip-${contact.id}`} type="button" onClick={() => toggleComposeParticipant(contact.id)} title={`Remove ${contact.name}`}>
                        <span>{contact.name}</span>
                        <Icon name="X" size={12} />
                      </ToChip>
                    ))}
                  </ToChipWrap>
                </ToBox>
                <SearchField
                  value={composeSearch}
                  onChange={(e) => setComposeSearch(e.target.value)}
                  placeholder="Search contacts to message..."
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {composeParticipantIds.length > 1 && (
                  <SearchField
                    value={composeGroupTitle}
                    onChange={(e) => setComposeGroupTitle(e.target.value)}
                    placeholder="Group name (optional)"
                  />
                )}
                <ContactPickList>
                  {composeContactsForList.map((contact) => {
                    const selected = composeParticipantIds.includes(contact.id);
                    return (
                      <ContactPickItem
                        key={`compose-contact-${contact.id}`}
                        type="button"
                        $selected={selected}
                        onClick={() => toggleComposeParticipant(contact.id)}
                        onDoubleClick={() => {
                          void quickStartContactByDoubleClick(contact);
                        }}
                      >
                        <div>
                          <ListItemTitle>{contact.name}</ListItemTitle>
                          <ListItemMeta>{contact.role || contact.team || 'Contact'}</ListItemMeta>
                        </div>
                        <div>{selected ? 'Selected' : 'Select'}</div>
                      </ContactPickItem>
                    );
                  })}
                </ContactPickList>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <AttachmentButton
                    type="button"
                    onClick={() => {
                      setIsComposeOpen(false);
                      setComposeParticipantIds([]);
                      setComposeSearch('');
                      setComposeGroupTitle('');
                    }}
                    disabled={isCreatingGroup || selectingContactId !== null}
                  >
                    Cancel
                  </AttachmentButton>
                  <ConversationAction
                    type="button"
                    disabled={composeParticipantIds.length === 0 || isCreatingGroup || selectingContactId !== null}
                    onClick={() => {
                      void startConversationFromCompose();
                    }}
                  >
                    Start Chat
                  </ConversationAction>
                </div>
              </ComposePanel>
            ) : (
              <MessagesArea ref={messagesAreaRef}>
          {messages.length === 0 ? (
            <EmptyWorkspace>
              <EmptyTitle>{activeContact?.name ? `Start the thread with ${activeContact.name}` : 'Open trade conversations, not blank space'}</EmptyTitle>
              <EmptyText>
                Keep this workspace alive while you review invoices, manifests, or compliance notes. Once messages arrive, this thread becomes the live decision trail for the shipment, payment, and customs milestones linked to it.
              </EmptyText>
              <MilestoneGrid>
                <MilestoneCard>
                  <MilestoneLabel>Payment</MilestoneLabel>
                  <MilestoneValue>Terms, proofs, and settlement blockers</MilestoneValue>
                </MilestoneCard>
                <MilestoneCard>
                  <MilestoneLabel>Customs</MilestoneLabel>
                  <MilestoneValue>Declarations, broker handoffs, and authority feedback</MilestoneValue>
                </MilestoneCard>
                <MilestoneCard>
                  <MilestoneLabel>Shipment</MilestoneLabel>
                  <MilestoneValue>Vessel timing, port readiness, and document holds</MilestoneValue>
                </MilestoneCard>
              </MilestoneGrid>
            </EmptyWorkspace>
          ) : messages.map((msg, index) => {
              const prevMsg = index > 0 ? messages[index - 1] : undefined;
              const own = Number(msg.sender_contact_id) === Number(resolvedSenderContactId);
              const messageKey = msg.id ? `msg-${msg.id}` : `msg-${msg.thread_id}-${msg.sender_contact_id}-${msg.created_at || 'na'}`;
              const currentDateLabel = formatMessageDateLabel(msg.created_at);
              const prevDateLabel = formatMessageDateLabel(prevMsg?.created_at);
              const showDateSeparator = Boolean(currentDateLabel && currentDateLabel !== prevDateLabel);
              const compactGroup = Boolean(
                prevMsg
                && Number(prevMsg.sender_contact_id) === Number(msg.sender_contact_id)
                && currentDateLabel === prevDateLabel
                && Math.abs(toEpoch(msg.created_at) - toEpoch(prevMsg.created_at)) <= 5 * 60 * 1000
              );
              const originalText = firstNonEmpty(msg.original_text, msg.text, msg.message_text);
              const translatedText = firstNonEmpty(msg.translated_text, msg.message_text);
              const incomingLanguage = normalizeLanguageCode(msg.source_language);
              const recipientLanguage = normalizeLanguageCode(msg.target_language || preferredLanguage);
              const languageDiffers = Boolean(incomingLanguage && recipientLanguage && incomingLanguage !== recipientLanguage);
              const originalAttachment = parseAttachmentMessage(originalText);
              const translatedAttachment = parseAttachmentMessage(translatedText);
              const attachment = originalAttachment || translatedAttachment;
              const originalCaption = originalAttachment?.caption || '';
              const translatedCaption = translatedAttachment?.caption || '';
              const originalAttachmentFallback = attachmentFallbackSummary(originalText);
              const translatedAttachmentFallback = attachmentFallbackSummary(translatedText);
              const sourceLanguageLabel = getLanguageLabel(msg.source_language);
              const hasOriginalPair = Boolean(
                !own &&
                languageDiffers &&
                !isUniversalPhrase(originalText) &&
                translatedText.trim() &&
                originalText.trim() &&
                translatedText.trim() !== originalText.trim()
              );
              const translatedBody = attachment ? translatedCaption : (translatedAttachmentFallback || translatedText);
              const originalBody = attachment ? originalCaption : (originalAttachmentFallback || originalText);
              const displayTranslatedBody = translatedBody.trim();
              const displayOriginalBody = originalBody.trim();
              const hasRenderableText = Boolean(displayTranslatedBody || displayOriginalBody);
              const primaryBody = own
                ? (displayOriginalBody || displayTranslatedBody)
                : (displayTranslatedBody || displayOriginalBody);
              const messageTime = formatMessageTime(msg.created_at);
              const senderLabel = firstNonEmpty(msg.sender_name, own ? senderIdentity?.name : '', own ? 'You' : 'Unknown');
              return (
                <React.Fragment key={messageKey}>
                  {showDateSeparator ? (
                    <MessageDateSeparator>
                      <MessageDateChip>{currentDateLabel}</MessageDateChip>
                    </MessageDateSeparator>
                  ) : null}
                <MessageCluster $own={own} $compact={compactGroup}>
                <MessageBubble $own={own}>
                  <MessageAttachmentStack>
                    {attachment?.kind === 'image' && <MessageImage src={attachment.url} alt={attachment.fileName || 'Shared image'} loading="lazy" />}
                    {attachment?.kind === 'audio' && <MessageAudio controls src={attachment.url} preload="none" />}
                    {attachment?.kind === 'audio' && (
                      <MessageAudioFallback href={attachment.url} target="_blank" rel="noreferrer">
                        <Icon name="Download" size={12} />
                        Open or download audio
                      </MessageAudioFallback>
                    )}
                    {attachment?.kind === 'document' && (
                      <MessageDocument href={attachment.url} target="_blank" rel="noreferrer">
                        <Icon name="FileText" size={14} />
                        {attachment.fileName || 'Open document'}
                      </MessageDocument>
                    )}
                  </MessageAttachmentStack>
                  {hasRenderableText ? <MessageText style={{ marginTop: attachment ? 8 : 0 }}>{primaryBody}</MessageText> : null}
                  <MessageTranslationBlock $expanded={hasOriginalPair} aria-hidden={!hasOriginalPair}>
                    {hasOriginalPair ? (
                      <>
                        <MessageFooterRow style={{ marginTop: 0 }}>
                          <MessageMetaBadge>
                            <MessageMetaSymbol aria-hidden="true">
                              <Icon name="FileText" size={11} />
                            </MessageMetaSymbol>
                            {sourceLanguageLabel}
                          </MessageMetaBadge>
                        </MessageFooterRow>
                        <MessageTranslationText>{originalBody}</MessageTranslationText>
                      </>
                    ) : null}
                  </MessageTranslationBlock>
                  {!compactGroup ? (
                  <MessageHeaderRow>
                    <MessageMetaBadge>{senderLabel}</MessageMetaBadge>
                    {messageTime ? <MessageMetaBadge>{messageTime}</MessageMetaBadge> : null}
                  </MessageHeaderRow>
                  ) : null}
                </MessageBubble>
                </MessageCluster>
                </React.Fragment>
              );
            })}
              </MessagesArea>
            )}

            {(availableChatsLoading || contactsLoading || error || identityMismatchWarning) && (
              <InlineStatus style={{ padding: '8px 12px' }}>
                {availableChatsLoading ? 'Loading chats...' : ''}
                {availableChatsLoading && contactsLoading ? ' • ' : ''}
                {contactsLoading ? 'Loading contacts...' : ''}
                {error ? ` • ${error}` : ''}
                {identityMismatchWarning ? ` • ${identityMismatchWarning}` : ''}
              </InlineStatus>
            )}

            {!isComposeOpen && (
              <InputBar>
            <ComposerShell>
            <ComposerTopRow $mode={mode}>
              <AttachmentButton $compact={compactWidgetMode} onClick={() => imageFileRef.current?.click()} title="Attach image" type="button">
                <Icon name="Image" size={15} />
                <AttachmentLabel $hidden={compactWidgetMode}>Image</AttachmentLabel>
              </AttachmentButton>
              <AttachmentButton $compact={compactWidgetMode} onClick={() => documentFileRef.current?.click()} title="Attach document" type="button">
                <Icon name="FileText" size={15} />
                <AttachmentLabel $hidden={compactWidgetMode}>Document</AttachmentLabel>
              </AttachmentButton>
              <AttachmentButton $compact={compactWidgetMode} onClick={() => void toggleAudioRecording()} title={isRecording ? 'Stop recording' : 'Record audio note'} type="button">
                <Icon name={isRecording ? 'Minus' : 'Upload'} size={15} />
                <AttachmentLabel $hidden={compactWidgetMode}>{isRecording ? 'Stop Recording' : 'Record Audio'}</AttachmentLabel>
              </AttachmentButton>
              {selectedAttachment && (
                <AttachmentChip>
                  <Icon
                    name={selectedAttachment.kind === 'image' ? 'Image' : selectedAttachment.kind === 'document' ? 'FileText' : 'Upload'}
                    size={12}
                  />
                  <AttachmentName>{selectedAttachment.file.name}</AttachmentName>
                  <AttachmentButton
                    onClick={() => setSelectedAttachment(null)}
                    title="Remove attachment"
                    type="button"
                    style={{ minHeight: 20, padding: '0 6px', fontSize: 10 }}
                  >
                    <Icon name="X" size={11} />
                  </AttachmentButton>
                </AttachmentChip>
              )}
            </ComposerTopRow>
            <Composer
              $mode={mode}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              placeholder={compactWidgetMode ? 'Message' : 'Type message and press Enter to send'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.ctrlKey) {
                  e.preventDefault();
                  void sendMessage();
                }
              }}
            />
            <HiddenFileInput
              ref={imageFileRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedAttachment({ file, kind: 'image' });
                }
                e.currentTarget.value = '';
              }}
            />
            <HiddenFileInput
              ref={documentFileRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ppt,.pptx"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedAttachment({ file, kind: 'document' });
                }
                e.currentTarget.value = '';
              }}
            />
          </ComposerShell>
          <Button
            onClick={() => void sendMessage()}
            disabled={(!messageInput.trim() && !selectedAttachment) || isSending}
            title={isSending ? 'Sending message' : 'Send message'}
            style={compactWidgetMode ? { minWidth: 46, padding: '0 12px' } : undefined}
          >
            <Icon name="Send" size={16} />
            {compactWidgetMode ? null : (isSending ? 'Sending...' : 'Send')}
          </Button>
              </InputBar>
            )}
          </ConversationPane>
        </TeamsShell>
    </MessagingContainer>
    </PageShell>
  );
};

export default MessagingPlatform;
